import { useCallback, useState } from "preact/hooks"
import { useRange } from "@nosto/search-js/preact/hooks"
import DualRange from "@/elements/DualRange/DualRange"
import Icon from "@/elements/Icon/Icon"
import { SearchStatsFacet } from "@nosto/nosto-js/client"
import styles from "./RangeFacet.module.css"
import { cl } from "@nosto/search-js/utils"

type Props = {
  facet: SearchStatsFacet
  /** Whether the facet is initially expanded (default: false) */
  defaultActive?: boolean
}

function isPriceFacet(facet: SearchStatsFacet): boolean {
  return (facet.name ?? "").toLowerCase().includes("price")
}

function formatValue(value: number | undefined, isPrice: boolean): string {
  if (value === undefined || Number.isNaN(value)) return ""
  if (!isPrice) return String(Math.round(value))
  const locale =
    typeof window !== "undefined" && window.Shopify?.locale && window.Shopify?.country
      ? `${window.Shopify.locale}-${window.Shopify.country}`
      : "en-US"
  const currency = window.Shopify?.currency?.active ?? "USD"
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0
    }).format(value)
  } catch {
    return `$${Math.round(value)}`
  }
}

export default function RangeFacet({ facet, defaultActive = false }: Props) {
  const { min, max, range, updateRange } = useRange(facet.id)
  const [active, setActive] = useState(defaultActive)
  const isSelected = min !== range[0] || max !== range[1]
  const isPrice = isPriceFacet(facet)
  const formatRangeLabel = useCallback((value: number) => formatValue(value, isPrice), [isPrice])

  const toggleActive = () => {
    setActive(!active)
  }

  return (
    <li className={cl(styles.dropdown, active && styles.active)}>
      <button
        className={styles.anchor}
        onClick={() => {
          toggleActive()
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            toggleActive()
          }
        }}
        aria-controls={`${facet.id}-range-menu`}
        aria-expanded={active}
        aria-label={`${active ? "Collapse" : "Expand"} ${facet.name} range filter`}
        type="button"
      >
        <span className={styles.title}>{facet.name}</span>
        {isSelected && <span className={styles.count}>{1}</span>}
        <span className={styles.icon}>
          <Icon name={active ? "minus" : "plus"} />
        </span>
      </button>
      <div className={styles.menu} id={`${facet.id}-range-menu`} aria-expanded={active}>
        <DualRange
          min={min}
          max={max}
          value={[range[0], range[1]]}
          onChange={updateRange}
          id={`ns-${facet.id}-range`}
          formatLabel={formatRangeLabel}
        />
      </div>
    </li>
  )
}
